import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Select,
  Input,
  Modal,
  Form,
  InputNumber,
  message,
  Tooltip,
  Popconfirm,
  Badge,
  Empty,
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SearchOutlined,
  BellOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  fetchStockAlerts,
  fetchStockAlertStats,
  checkStockAlerts,
  resolveStockAlert,
  ignoreStockAlert,
  setStockAlertThresholds,
  fetchStockAlertThresholds,
  cleanupStockAlerts,
} from '../../api/stockAlert';

const { Option } = Select;

const levelMap = {
  critical: { text: '严重', color: 'red' },
  high: { text: '高', color: 'volcano' },
  medium: { text: '中', color: 'orange' },
  low: { text: '低', color: 'gold' },
};

const typeMap = {
  out_of_stock: { text: '缺货', color: 'red' },
  low_stock: { text: '低库存', color: 'orange' },
  overstock: { text: '库存过多', color: 'blue' },
};

const StockAlertPage = () => {
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total: 0, byType: {}, byLevel: {} });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('active');
  const [alertType, setAlertType] = useState('');
  const [alertLevel, setAlertLevel] = useState('');
  const [keyword, setKeyword] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm] = Form.useForm();
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadData = async (nextPage = page, nextLimit = limit) => {
    setLoading(true);
    try {
      const res = await fetchStockAlerts({
        page: nextPage,
        limit: nextLimit,
        status,
        alert_type: alertType,
        alert_level: alertLevel,
      });
      if (res.success) {
        setAlerts(res.data.alerts || []);
        setTotal(res.data.total || 0);
      }
      const statsRes = await fetchStockAlertStats();
      if (statsRes.success) {
        setStats(statsRes.data || { total: 0, byType: {}, byLevel: {} });
      }
    } catch (error) {
      message.error('加载库存预警失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, status, alertType, alertLevel]);

  const filteredAlerts = useMemo(() => {
    if (!keyword.trim()) return alerts;
    const lower = keyword.trim().toLowerCase();
    return alerts.filter((item) => {
      const productName = item.product?.name || '';
      return [item.message, productName, item.alert_type, item.alert_level]
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(lower));
    });
  }, [alerts, keyword]);

  const handleCheck = async () => {
    setLoading(true);
    try {
      const res = await checkStockAlerts();
      if (res.success) {
        message.success(`已检查库存，新增/更新 ${res.data.alert_count || 0} 条预警`);
        await loadData(page, limit);
      }
    } catch (error) {
      message.error('检查库存失败');
    } finally {
      setLoading(false);
    }
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    setSettingsLoading(true);
    try {
      const res = await fetchStockAlertThresholds();
      const thresholds = res.success ? res.data : {};
      settingsForm.setFieldsValue({
        low_stock: thresholds.low_stock ?? 10,
        critical_stock: thresholds.critical_stock ?? 5,
        overstock: thresholds.overstock ?? 1000,
      });
    } catch (error) {
      settingsForm.setFieldsValue({
        low_stock: 10,
        critical_stock: 5,
        overstock: 1000,
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      const values = await settingsForm.validateFields();
      const res = await setStockAlertThresholds(values);
      if (res.success) {
        message.success('预警阈值已更新');
        setSettingsOpen(false);
      }
    } catch (error) {
      if (!error?.errorFields) message.error('保存失败');
    }
  };

  const handleCleanup = async () => {
    try {
      const res = await cleanupStockAlerts({ days: 30 });
      if (res.success) {
        message.success(`已清理 ${res.data.deleted_count || 0} 条旧预警`);
        await loadData(page, limit);
      }
    } catch (error) {
      message.error('清理失败');
    }
  };

  const columns = [
    {
      title: '商品',
      key: 'product',
      render: (_, record) => (
        <Space>
          {record.product?.main_image && (
            <img
              src={record.product.main_image}
              alt={record.product?.name}
              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div>
            <div style={{ fontWeight: 500 }}>{record.product?.name || '-'}</div>
            <div style={{ fontSize: 12, color: '#999' }}>ID: {record.product?.id || '-'}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '预警类型',
      dataIndex: 'alert_type',
      render: (value) => <Tag color={typeMap[value]?.color || 'default'}>{typeMap[value]?.text || value}</Tag>,
    },
    {
      title: '级别',
      dataIndex: 'alert_level',
      render: (value) => <Tag color={levelMap[value]?.color || 'default'}>{levelMap[value]?.text || value}</Tag>,
    },
    {
      title: '当前库存',
      dataIndex: 'current_stock',
      render: (value) => <Badge count={value} style={{ backgroundColor: value <= 0 ? '#ff4d4f' : '#1890ff' }} />,
    },
    {
      title: '阈值',
      dataIndex: 'threshold_value',
    },
    {
      title: '预警信息',
      dataIndex: 'message',
      ellipsis: true,
      render: (text) => <Tooltip title={text}>{text}</Tooltip>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => {
        const map = {
          active: { text: '活跃', color: 'red' },
          resolved: { text: '已解决', color: 'green' },
          ignored: { text: '已忽略', color: 'default' },
        };
        return <Tag color={map[value]?.color || 'default'}>{map[value]?.text || value}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="确认标记为已解决？"
            onConfirm={async () => {
              try {
                const res = await resolveStockAlert(record.id, { note: '人工确认已处理' });
                if (res.success) {
                  message.success('已标记为解决');
                  await loadData(page, limit);
                }
              } catch (error) {
                message.error('操作失败');
              }
            }}
          >
            <Button size="small" type="link" icon={<CheckCircleOutlined />}>解决</Button>
          </Popconfirm>
          <Popconfirm
            title="确认忽略此预警？"
            onConfirm={async () => {
              try {
                const res = await ignoreStockAlert(record.id, { note: '人工忽略' });
                if (res.success) {
                  message.success('已忽略');
                  await loadData(page, limit);
                }
              } catch (error) {
                message.error('操作失败');
              }
            }}
          >
            <Button size="small" type="link" danger icon={<StopOutlined />}>忽略</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="workspace-page">
      <div className="workspace-header">
        <div>
          <h1 className="workspace-title">低库存提醒</h1>
          <p className="workspace-description">监控库存异常，及时补货或处理滞销积压</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => loadData(page, limit)}>刷新</Button>
          <Button icon={<BellOutlined />} onClick={handleCheck}>手动检查库存</Button>
          <Button icon={<SettingOutlined />} onClick={openSettings}>预警阈值</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} className="workspace-summary">
        <Col span={6}><Card><Statistic title="活跃预警" value={stats.total || 0} valueStyle={{ color: '#fa541c' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="缺货" value={stats.byType?.out_of_stock || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="低库存" value={stats.byType?.low_stock || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="库存过多" value={stats.byType?.overstock || 0} valueStyle={{ color: '#1890ff' }} /></Card></Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space wrap>
            <Select value={status} onChange={setStatus} style={{ width: 120 }}>
              <Option value="active">活跃</Option>
              <Option value="resolved">已解决</Option>
              <Option value="ignored">已忽略</Option>
              <Option value="">全部</Option>
            </Select>
            <Select value={alertType} onChange={setAlertType} allowClear placeholder="预警类型" style={{ width: 120 }}>
              <Option value="low_stock">低库存</Option>
              <Option value="out_of_stock">缺货</Option>
              <Option value="overstock">库存过多</Option>
            </Select>
            <Select value={alertLevel} onChange={setAlertLevel} allowClear placeholder="预警级别" style={{ width: 120 }}>
              <Option value="critical">严重</Option>
              <Option value="high">高</Option>
              <Option value="medium">中</Option>
              <Option value="low">低</Option>
            </Select>
            <Input
              prefix={<SearchOutlined />}
              placeholder="搜索商品或信息"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              style={{ width: 240 }}
            />
          </Space>
          <Popconfirm
            title="确认清理 30 天前的已处理预警？"
            onConfirm={handleCleanup}
          >
            <Button danger>清理历史预警</Button>
          </Popconfirm>
        </Space>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredAlerts}
          columns={columns}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextLimit) => {
              setPage(nextPage);
              setLimit(nextLimit);
            },
          }}
          locale={{ emptyText: <Empty description="暂无库存预警" /> }}
        />
      </Card>

      <Modal
        title="预警阈值设置"
        open={settingsOpen}
        onOk={saveSettings}
        onCancel={() => setSettingsOpen(false)}
        confirmLoading={settingsLoading}
        okText="保存"
        cancelText="取消"
      >
        <Form form={settingsForm} layout="vertical">
          <Form.Item name="low_stock" label="低库存阈值" rules={[{ required: true }]}>
            <InputNumber min={1} max={9999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="critical_stock" label="严重缺货阈值" rules={[{ required: true }]}>
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="overstock" label="库存过多阈值" rules={[{ required: true }]}>
            <InputNumber min={1} max={999999} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StockAlertPage;
